// Migration configurations for all model types

import { MigrationManager, safeGet, removeProperties } from "../migrationUtils";
import { UserModel } from "@/models/UserModels";
import { LeagueMemberModel, LeagueModel } from "@/models/LeagueModels";
import { RoundModel } from "@/models/RoundModels";
import { UserDashboardModel } from "@/models/UserDashboardModels";
import { LeagueMembershipModel } from "@/models/LeagueMembershipModels";
import { LeagueMemberResultModel, LeagueResultModel } from "@/models/LeagueResultModels";
import { Timestamp } from "firebase/firestore";
import { resolveType } from "@/services/ServiceContainer";
import { UserService } from "@/services/UserService";
import { LeagueMembershipService } from "@/services/LeagueMembershipService";

// Static cache for expensive operations during migrations
const migrationCache = new Map<string, any[]>();

// Helper function to get cached data or fetch and cache it
async function getCachedOrFetch<T>(cacheKey: string, fetchFunction: () => Promise<T[]>): Promise<T[]> {
    if (migrationCache.has(cacheKey)) {
        return migrationCache.get(cacheKey) as T[];
    }

    const data = await fetchFunction();
    migrationCache.set(cacheKey, data);
    return data;
}

// User Model Migrations
export function createUserMigrationManager(): MigrationManager<UserModel> {
    const manager = new MigrationManager<UserModel>(2);

    // Example migration: Add schemaVersion field to existing documents
    manager.registerMigration({
        version: 1,
        description: "Add schemaVersion field and ensure all required fields exist",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            migrated.uid = safeGet(data, 'uid', '');
            migrated.email = safeGet(data, 'email', '');
            migrated.encodedEmail = safeGet(data, 'encodedEmail', '');
            migrated.firstName = safeGet(data, 'firstName', '');
            migrated.lastName = safeGet(data, 'lastName', '');
            migrated.nickName = safeGet(data, 'nickName', '');
            migrated.suffix = safeGet(data, 'suffix', '');
            migrated.handicap = safeGet(data, 'handicap', 0);

            // Ensure preferences object exists
            if (!migrated.preferences) {
                migrated.preferences = {
                    theme: 'light',
                    notifications: true,
                    language: 'en'
                };
            }

            // Ensure activeLeagues object exists
            if (!migrated.activeLeagues) {
                migrated.activeLeagues = {};
            }

            return migrated as UserModel;
        }
    });

    // Fix timestamp format inconsistency in user timestamp fields
    manager.registerMigration({
        version: 2,
        description: "Convert string timestamps to Firestore Timestamp objects in user fields",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Convert string timestamps to Firestore Timestamp objects or null
            if (typeof migrated.createdAt === 'string') {
                if (migrated.createdAt === '') {
                    migrated.createdAt = null;
                } else {
                    migrated.createdAt = Timestamp.fromDate(new Date(migrated.createdAt));
                }
            } else if (migrated.createdAt === undefined) {
                migrated.createdAt = null;
            }
            if (typeof migrated.updatedAt === 'string') {
                if (migrated.updatedAt === '') {
                    migrated.updatedAt = null;
                } else {
                    migrated.updatedAt = Timestamp.fromDate(new Date(migrated.updatedAt));
                }
            } else if (migrated.updatedAt === undefined) {
                migrated.updatedAt = null;
            }

            return migrated as UserModel;
        }
    });

    return manager;
}

// League Model Migrations
export function createLeagueMigrationManager(): MigrationManager<LeagueModel> {
    const manager = new MigrationManager<LeagueModel>(4);

    // Example migration: Add schemaVersion field and ensure all required fields exist
    manager.registerMigration({
        version: 1,
        description: "Add schemaVersion field and ensure all required fields exist",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            migrated.id = safeGet(data, 'id', '');
            migrated.name = safeGet(data, 'name', '');
            migrated.adminUids = safeGet(data, 'adminUids', {});
            migrated.courseId = safeGet(data, 'courseId', '');
            migrated.schedule = safeGet(data, 'schedule', []);
            migrated.handicapMethod = safeGet(data, 'handicapMethod', 'USGA');
            migrated.skinsType = safeGet(data, 'skinsType', 'NONE');
            migrated.startDayId = safeGet(data, 'startDayId', '');
            migrated.endDayId = safeGet(data, 'endDayId', '');
            migrated.flights = safeGet(data, 'flights', []);
            migrated.members = safeGet(data, 'members', {});
            migrated.codeOfConduct = safeGet(data, 'codeOfConduct', null);

            return migrated as LeagueModel;
        }
    });

    // Example migration: Add schemaVersion field and ensure all required fields exist
    manager.registerMigration({
        version: 2,
        description: "Add initialHandicap and initialFlightId to members",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            migrated.members = safeGet(data, 'members', {});
            Object.values(migrated.members).forEach((member: any) => {
                member.initialHandicap = safeGet(member, 'handicap', null);
                member.initialFlightId = safeGet(member, 'flightId', null);
            });

            return migrated as LeagueModel;
        }
    });


    // Example migration: Assign uid to members
    manager.registerMigration({
        version: 3,
        description: "Assign uid to members",
        migrate: async (data: any, _) => {
            const migrated = { ...data };
            for (const member of Object.values(migrated.members)) {
                const typedMember = member as LeagueMemberModel;
                const leagueMembershipService = resolveType(LeagueMembershipService);
                const membershipModel = await leagueMembershipService.get(typedMember.encodedEmail);
                typedMember.uid = membershipModel?.uid || typedMember.uid || '';
            }

            return migrated as LeagueModel;
        }
    });

    // Fix timestamp format inconsistency in schedule fields
    manager.registerMigration({
        version: 4,
        description: "Convert string timestamps to Firestore Timestamp objects in schedule fields",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure schedule exists
            migrated.schedule = safeGet(data, 'schedule', []);

            // Process each schedule item
            migrated.schedule.forEach((scheduleItem: any) => {
                // Convert string timestamps to Firestore Timestamp objects or null
                if (typeof scheduleItem.lockedAt === 'string') {
                    if (scheduleItem.lockedAt === '') {
                        scheduleItem.lockedAt = null;
                    } else {
                        scheduleItem.lockedAt = Timestamp.fromDate(new Date(scheduleItem.lockedAt));
                    }
                }
                if (typeof scheduleItem.activeAt === 'string') {
                    if (scheduleItem.activeAt === '') {
                        scheduleItem.activeAt = null;
                    } else {
                        scheduleItem.activeAt = Timestamp.fromDate(new Date(scheduleItem.activeAt));
                    }
                }
                if (typeof scheduleItem.completedAt === 'string') {
                    if (scheduleItem.completedAt === '') {
                        scheduleItem.completedAt = null;
                    } else {
                        scheduleItem.completedAt = Timestamp.fromDate(new Date(scheduleItem.completedAt));
                    }
                }
                if (typeof scheduleItem.updatedAt === 'string') {
                    if (scheduleItem.updatedAt === '') {
                        scheduleItem.updatedAt = null;
                    } else {
                        scheduleItem.updatedAt = Timestamp.fromDate(new Date(scheduleItem.updatedAt));
                    }
                }
            });

            return migrated as LeagueModel;
        }
    });
    return manager;
}

// Round Model Migrations
export function createRoundMigrationManager(): MigrationManager<RoundModel> {
    const manager = new MigrationManager<RoundModel>(4);

    // Example migration: Add schemaVersion field and ensure all required fields exist
    manager.registerMigration({
        version: 1,
        description: "Add schemaVersion field and ensure all required fields exist",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            migrated.id = safeGet(data, 'id', '');
            migrated.creatorId = safeGet(data, 'creatorId', '');
            migrated.setup = safeGet(data, 'setup', {
                courseId: '',
                courseName: '',
                selectedTee: '',
                flightId: null,
                startingHole: 1,
                holesToPlay: 'all18'
            });
            migrated.users = safeGet(data, 'users', {});
            migrated.status = safeGet(data, 'status', null);

            return migrated as RoundModel;
        }
    });

    manager.registerMigration({
        version: 2,
        description: "Convert context to setup",
        migrate: async (data: any, _) => {
            const migrated = { ...data };
            if (migrated && migrated.context) {
                migrated.setup = migrated.setup || {};
                migrated.setup.league = migrated.setup.league || {};
                migrated.setup.league.id = migrated.context.league?.id || '';
                migrated.setup.league.dayId = migrated.context.league?.dayId || '';
                migrated.setup.type = migrated.context.type || 'casual';
                delete migrated.context;
            }
            return migrated as RoundModel;
        }
    });

    manager.registerMigration({
        version: 3,
        description: "Remove course",
        migrate: async (data: any, _) => {
            const migrated = { ...data };

            // console.log('Migration v3 - Before deletion:');
            // console.log('  - Has course property:', 'course' in migrated);
            // console.log('  - Has setup.course property:', migrated.setup && 'course' in migrated.setup);

            if (migrated && migrated.course) {
                // console.log('  - Deleting migrated.course');
                delete migrated.course;
            }
            if (migrated && migrated.setup && migrated.setup.course) {
                // console.log('  - Deleting migrated.setup.course');
                delete migrated.setup.course;
            }

            // console.log('Migration v3 - After deletion:');
            // console.log('  - Has course property:', 'course' in migrated);
            // console.log('  - Has setup.course property:', migrated.setup && 'course' in migrated.setup);

            return migrated as RoundModel;
        }
    });

    // Fix timestamp format inconsistency in round timestamp fields
    manager.registerMigration({
        version: 4,
        description: "Convert string timestamps to Firestore Timestamp objects in round fields",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Convert string timestamps to Firestore Timestamp objects or null
            if (typeof migrated.createdAt === 'string') {
                if (migrated.createdAt === '') {
                    migrated.createdAt = null;
                } else {
                    migrated.createdAt = Timestamp.fromDate(new Date(migrated.createdAt));
                }
            }
            if (typeof migrated.startedAt === 'string') {
                if (migrated.startedAt === '') {
                    migrated.startedAt = null;
                } else {
                    migrated.startedAt = Timestamp.fromDate(new Date(migrated.startedAt));
                }
            }
            if (typeof migrated.updatedAt === 'string') {
                if (migrated.updatedAt === '') {
                    migrated.updatedAt = null;
                } else {
                    migrated.updatedAt = Timestamp.fromDate(new Date(migrated.updatedAt));
                }
            }
            if (typeof migrated.completedAt === 'string') {
                if (migrated.completedAt === '') {
                    migrated.completedAt = null;
                } else {
                    migrated.completedAt = Timestamp.fromDate(new Date(migrated.completedAt));
                }
            }
            if (typeof migrated.lockedAt === 'string') {
                if (migrated.lockedAt === '') {
                    migrated.lockedAt = null;
                } else {
                    migrated.lockedAt = Timestamp.fromDate(new Date(migrated.lockedAt));
                }
            }

            return migrated as RoundModel;
        }
    });

    return manager;
}

// UserDashboard Model Migrations
export function createUserDashboardMigrationManager(): MigrationManager<UserDashboardModel> {
    const manager = new MigrationManager<UserDashboardModel>(5);

    // Version 1: Add schemaVersion field and ensure all required fields exist
    manager.registerMigration({
        version: 1,
        description: "Add schemaVersion field and ensure all required fields exist",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            migrated.userId = safeGet(data, 'userId', '');
            migrated.updatedAt = safeGet(data, 'updatedAt', Timestamp.now());
            migrated.casualRounds = safeGet(data, 'casualRounds', []);
            migrated.leagueRounds = safeGet(data, 'leagueRounds', {});
            migrated.tournamentRounds = safeGet(data, 'tournamentRounds', []);

            // Optional fields
            if (data.activeRoundId) {
                migrated.activeRoundId = data.activeRoundId;
            }

            return migrated as UserDashboardModel;
        }
    });

    // Version 2: Rename leagueRounds[x].rounds[y].context to .setup
    manager.registerMigration({
        version: 2,
        description: "Rename leagueRounds[x].rounds[y].context to .setup",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure leagueRounds exists
            migrated.leagueRounds = safeGet(data, 'leagueRounds', {});

            Object.values(migrated.leagueRounds).forEach((leagueRounds: any) => {
                if (leagueRounds.rounds) {
                    leagueRounds.rounds.forEach((round: any) => {
                        if (round && round.context && !round.setup) {
                            round.setup = round.context;
                            delete round.context;
                        }
                    });
                }
            });

            if (migrated.course) {
                delete migrated.course;
            }

            return migrated as UserDashboardModel;
        }
    });

    // Version 3: Add missing fields and fetch course data
    manager.registerMigration({
        version: 3,
        description: "Add missing user handicap, course par, and per-hole par data",
        migrate: async (data: any, _) => {
            const migrated = { ...data };
            // const courseService = resolveType(CourseService);
            // const leagueService = resolveType(LeagueService);
            // const userService = resolveType(UserService);

            // // Ensure leagueRounds exists
            // migrated.leagueRounds = safeGet(data, 'leagueRounds', {});
            // const user = await userService.get(migrated.userId);
            // const encodedEmail = user?.encodedEmail || '';

            // let course = null;
            // let league = null;

            // // Process league rounds
            // if (migrated.leagueRounds) {
            //     for (const leagueData of Object.values(migrated.leagueRounds)) {
            //         const typedLeagueData = leagueData as { rounds?: any[] };
            //         if (typedLeagueData.rounds) {
            //             for (const round of typedLeagueData.rounds) {
            //                 if (!course) {
            //                     course = await courseService.get(round.courseId);
            //                 }
            //                 if (round && round.courseId && !round.par) {
            //                     try {
            //                         if (course && course.scorecard && course.scorecard.holes) {
            //                             round.par = course.scorecard.holes.reduce((sum, hole) => sum + hole.par, 0);

            //                         }
            //                     } catch (error) {
            //                         console.error(`Failed to fetch data for round ${round.courseId}:`, error);
            //                     }
            //                 }

            //                 for (const hole of course!.scorecard.holes) {
            //                     if (hole.par) {
            //                         const h = round.holes.find((h1: any) => h1.hole == hole.number);
            //                         if (h) {
            //                             h.par = hole.par;
            //                             h.mensHandicap9 = hole.mensHandicap9 || 0;
            //                         }
            //                     }
            //                 }

            //                 if (!round.handicap) {
            //                     if (!league) {
            //                         league = await leagueService.get(round.setup.league.id);
            //                     }
            //                     if (league) {
            //                         const m = league.members[encodedEmail];
            //                         if (m && !round.handicap) {
            //                             round.handicap = m.handicap || 0;
            //                         }
            //                     }
            //                 }
            //             }
            //         }
            //     }
            // }

            // if (migrated.course) {
            //     delete migrated.course;
            // }

            return migrated as UserDashboardModel;
        }
    });

    manager.registerMigration({
        version: 4,
        description: "Add encodedEmail property",
        migrate: async (data: any, _) => {
            const migrated = { ...data };
            const userService = resolveType(UserService);
            const userModel = await userService.get(migrated.userId);
            migrated.encodedEmail = userModel?.encodedEmail || null;

            return migrated as UserDashboardModel;
        }
    });

    // Fix timestamp format inconsistency in user dashboard timestamp fields
    manager.registerMigration({
        version: 5,
        description: "Convert string timestamps to Firestore Timestamp objects in user dashboard fields",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Convert string timestamps to Firestore Timestamp objects or null
            if (typeof migrated.updatedAt === 'string') {
                if (migrated.updatedAt === '') {
                    migrated.updatedAt = null;
                } else {
                    migrated.updatedAt = Timestamp.fromDate(new Date(migrated.updatedAt));
                }
            }

            return migrated as UserDashboardModel;
        }
    });

    return manager;
}

// LeagueMembership Model Migrations
export function createLeagueMembershipMigrationManager(): MigrationManager<LeagueMembershipModel> {
    const manager = new MigrationManager<LeagueMembershipModel>(2);

    // Example migration: Add schemaVersion field and ensure all required fields exist
    manager.registerMigration({
        version: 1,
        description: "Add schemaVersion field and ensure all required fields exist",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            migrated.encodedEmail = safeGet(data, 'encodedEmail', '');
            migrated.email = safeGet(data, 'email', '');
            migrated.displayName = safeGet(data, 'displayName', '');
            migrated.firstName = safeGet(data, 'firstName', '');
            migrated.lastName = safeGet(data, 'lastName', '');
            migrated.phoneNumber = safeGet(data, 'phoneNumber', '');
            migrated.uid = safeGet(data, 'uid', '');
            migrated.leagues = safeGet(data, 'leagues', {});

            return migrated as LeagueMembershipModel;
        }
    });

    manager.registerMigration({
        version: 2,
        description: "Add actual uid value, if possible",
        migrate: async (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            const userService = resolveType(UserService);
            const userModel = await userService.getByEncodedEmail(data.encodedEmail);
            migrated.uid = userModel?.uid || '';

            return migrated as LeagueMembershipModel;
        }
    });

    return manager;
}

// LeagueResult Model Migrations
export function createLeagueResultMigrationManager(): MigrationManager<LeagueResultModel> {
    const manager = new MigrationManager<LeagueResultModel>(2);

    // Example migration: Add schemaVersion field and ensure all required fields exist
    manager.registerMigration({
        version: 1,
        description: "Add schemaVersion field and ensure all required fields exist",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Ensure all required fields exist with defaults
            migrated.id = safeGet(data, 'id', '');
            migrated.leagueId = safeGet(data, 'leagueId', '');
            migrated.dayId = safeGet(data, 'dayId', '');
            migrated.members = safeGet(data, 'members', {});
            migrated.pinPrizes = safeGet(data, 'pinPrizes', []);
            migrated.grossSkinPrizes = safeGet(data, 'grossSkinPrizes', []);
            migrated.netSkinPrizes = safeGet(data, 'netSkinPrizes', []);

            return migrated as LeagueResultModel;
        }
    });

    // Example migration: Assign uid to members
    manager.registerMigration({
        version: 2,
        description: "Assign uid to members",
        migrate: async (data: any, _) => {
            const migrated = { ...data };
            const leagueMembershipService = resolveType(LeagueMembershipService);
            const allMemberships = await getCachedOrFetch<LeagueMembershipModel>(
                'leagueMemberships_all',
                () => leagueMembershipService.listAll()
            );
            for (const member of Object.values(migrated.members)) {
                const typedMember = member as LeagueMemberResultModel;
                const membershipModel = allMemberships.find(m => m.encodedEmail === typedMember.encodedEmail);
                typedMember.uid = membershipModel?.uid || typedMember.uid || '';
            }

            return migrated as LeagueResultModel;
        }
    });

    return manager;
}

// Example of how to add more complex migrations:
// This shows how you might handle a breaking change in the data structure

export function createExampleComplexMigrationManager(): MigrationManager<LeagueModel> {
    const manager = new MigrationManager<LeagueModel>(2);

    // Version 1: Basic migration
    manager.registerMigration({
        version: 1,
        description: "Add schemaVersion field and ensure all required fields exist",
        migrate: (data: any, _) => {
            const migrated = { ...data };
            migrated.id = safeGet(data, 'id', '');
            migrated.name = safeGet(data, 'name', '');
            migrated.adminUids = safeGet(data, 'adminUids', {});
            migrated.courseId = safeGet(data, 'courseId', '');
            migrated.schedule = safeGet(data, 'schedule', []);
            migrated.handicapMethod = safeGet(data, 'handicapMethod', 'USGA');
            migrated.skinsType = safeGet(data, 'skinsType', 'NONE');
            migrated.startDayId = safeGet(data, 'startDayId', '');
            migrated.endDayId = safeGet(data, 'endDayId', '');
            migrated.flights = safeGet(data, 'flights', []);
            migrated.members = safeGet(data, 'members', {});
            migrated.codeOfConduct = safeGet(data, 'codeOfConduct', null);

            return migrated as LeagueModel;
        }
    });

    // Version 2: Example of a breaking change migration
    // This shows how you might handle renaming fields or restructuring data
    manager.registerMigration({
        version: 2,
        description: "Rename adminIds to adminUids and restructure member data",
        migrate: (data: any, _) => {
            const migrated = { ...data };

            // Example: Rename adminIds to adminUids if it exists
            if (data.adminIds && !data.adminUids) {
                migrated.adminUids = data.adminIds;
                removeProperties(migrated, ['adminIds']);
            }

            // Example: Restructure member data if needed
            if (data.members) {
                const updatedMembers: Record<string, any> = {};

                for (const [key, member] of Object.entries(data.members)) {
                    const updatedMember = { ...(member as any) };

                    // Example: Add missing fields to members
                    if (!updatedMember.joinedAt) {
                        updatedMember.joinedAt = {
                            seconds: Math.floor(Date.now() / 1000),
                            nanoseconds: 0
                        };
                    }

                    updatedMembers[key] = updatedMember;
                }

                migrated.members = updatedMembers;
            }

            return migrated as LeagueModel;
        }
    });

    return manager;
} 